// src/components/Booking/BookingGrid.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiCalendar as FiBookNow,
} from "react-icons/fi";

const BookingCard = ({ booking, onBookNow, primaryColor }) => {
  const [isBooking, setIsBooking] = useState(false);

  const handleBookNow = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (onBookNow) {
      setIsBooking(true);
      await onBookNow(booking);
      setIsBooking(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "Flexible";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0
      ? `${hours}h ${mins}m`
      : `${hours} hour${hours > 1 ? "s" : ""}`;
  };

  const getLocationIcon = (type) => {
    return type === "online" ? "💻" : "📍";
  };

  const getLocationText = (type) => {
    return type === "online" ? "Online / Virtual" : "In-Person";
  };

  return (
    <motion.div
      className="booking-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Link to={`/booking/${booking.ProductID}`} className="booking-link">
        <div className="booking-image-container">
          <img
            src={booking.ImageUrl || "https://via.placeholder.com/300"}
            alt={booking.Name}
          />
          {booking.Duration && (
            <div className="booking-duration-badge">
              <FiClock /> {formatDuration(booking.Duration)}
            </div>
          )}
        </div>

        <div className="booking-info">
          <h3 className="booking-name">{booking.Name}</h3>

          {booking.Description && (
            <p className="booking-description">
              {booking.Description.length > 80
                ? `${booking.Description.substring(0, 80)}...`
                : booking.Description}
            </p>
          )}

          <div className="booking-meta">
            <span className="booking-location">
              {getLocationIcon(booking.LocationType)}{" "}
              {getLocationText(booking.LocationType)}
            </span>
            {booking.Duration && (
              <span className="booking-duration">
                <FiClock /> {formatDuration(booking.Duration)}
              </span>
            )}
          </div>

          <div className="booking-price-section">
            <span className="booking-price">
              R{booking.SellingPrice?.toLocaleString()}
            </span>
            {booking.BookingRequired && (
              <span className="booking-required-badge">Booking Required</span>
            )}
          </div>
        </div>
      </Link>

      <div className="booking-actions-bottom">
        <button
          className="booking-btn-book-now"
          onClick={handleBookNow}
          disabled={isBooking}
          style={{ backgroundColor: primaryColor }}
        >
          <FiBookNow />
          {isBooking ? "Processing..." : "Book Now"}
        </button>
      </div>
    </motion.div>
  );
};

const BookingGrid = ({
  bookings,
  loading,
  onBookNow,
  primaryColor,
  cardStyle = "standard",
}) => {
  if (loading) {
    return (
      <div className="loading-grid">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="skeleton-card">
            <div className="skeleton-image"></div>
            <div className="skeleton-text"></div>
            <div className="skeleton-text short"></div>
            <div className="skeleton-button"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!bookings || bookings.length === 0) {
    return (
      <motion.div
        className="empty-state"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="empty-icon">📅</div>
        <h3>No services available</h3>
        <p>Check back later for new appointments and services</p>
      </motion.div>
    );
  }

  return (
    <div className={`booking-grid booking-grid--${cardStyle}`}>
      {bookings.map((booking) => (
        <BookingCard
          key={booking.ProductID}
          booking={booking}
          onBookNow={onBookNow}
          primaryColor={primaryColor}
        />
      ))}
    </div>
  );
};

export default BookingGrid;
